/*
 * @Author: www
 * @Date: 2020-02-26 15:45:14
 * @Last Modified by: www
 * @Last Modified time: 2020-04-07 10:14:12
 */

import { cloneDeep } from 'lodash';
import { getDeptList, getRoleList } from '@/api/crm.js';
import {
  formatTreeLevel,
  formatTree,
  findNodeFullPath,
  findSubNodes,
  updateNodeOpen,
  upateNodePathsOpen,
  addSubNode,
  updateNode,
  deleteNode,
  upateNodePathsUserCount,
  updateSubNodeOrder,
} from '@/utils/tree.js';

// 公用
export const RESET = 'RESET';

// 部门操作
export const UPDATE_DEPT_LOADING = 'UPDATE_DEPT_LOADING'; // 部门获取中
export const INIT_DEPTS = 'INIT_DEPTS';
export const ADD_SUB_DEPT = 'ADD_SUB_DEPT'; // 新增子部门
export const UPDATE_DEPT = 'UPDATE_DEPT'; // 更新部门
export const DELETE_DEPT = 'DELETE_DEPT'; // 删除部门
export const SET_CURRENT_DEPT_ID = 'SET_CURRENT_DEPT_ID'; // 设置当前部门id
export const SET_DEPT_NODE_OPEN = 'SET_DEPT_NODE_OPEN'; // 设置部门树节点展开与收起状态
export const SET_DEPT_NODE_PATHS_OPEN = 'SET_DEPT_NODE_PATHS_OPEN'; // 设置部门节点全路径的展开与收起状态
export const UPATE_NODE_PATHS_USER_COUNT = 'UPATE_NODE_PATHS_USER_COUNT'; // 更新全路径上的部门用户数
export const UPDATE_SUB_DEPT_ORDER = 'UPDATE_SUB_DEPT_ORDER'; // 调整当前部门的子部门顺序

// 角色操作
export const UPDATE_ROLE_LOADING = 'UPDATE_ROLE_LOADING'; // 角色获取中
export const INIT_ROLES = 'INIT_ROLES'; // 初始化角色列表
export const SET_CURRENT_ROLE_ID = 'SET_CURRENT_ROLE_ID'; // 设置当前角色id
export const ADD_ROLE = 'ADD_ROLE'; // 新增角色
export const UPDATE_ROLE = 'UPDATE_ROLE'; // 更新角色
export const DELETE_ROLE = 'DELETE_ROLE'; // 删除角色

const initState = {
  deptTree: {}, // 根节点对象
  currentDeptId: null, // 当前部门id
  deptLoading: false, // 部门获取中状态
  roleList: [], // 角色列表
  currentRoleId: null, // 当前角色id
  roleLoading: false, // 角色获取中状态
};

const mutations = {
  [UPDATE_DEPT_LOADING](state, loading) {
    state.deptLoading = loading;
  },
  [UPDATE_ROLE_LOADING](state, loading) {
    state.roleLoading = loading;
  },
  [INIT_DEPTS](state, depts) {
    state.deptTree = depts;
  },
  [SET_CURRENT_DEPT_ID](state, deptId) {
    state.currentDeptId = deptId;
  },
  [SET_DEPT_NODE_OPEN](state, { id, isOpen }) {
    updateNodeOpen(state.deptTree, { id, isOpen });
  },
  [SET_DEPT_NODE_PATHS_OPEN](state, { id, isOpen }) {
    upateNodePathsOpen(state.deptTree, { id, isOpen });
  },
  [ADD_SUB_DEPT](state, dept) {
    addSubNode(state.deptTree, dept);
  },
  [UPDATE_DEPT](state, { deptId, deptName, newParentDeptId, oldParentDeptId }) {
    updateNode(state.deptTree, { deptId, deptName, newParentDeptId, oldParentDeptId });
  },
  [DELETE_DEPT](state, { parentId, id }) {
    deleteNode(state.deptTree, { parentId, id });
  },
  [UPATE_NODE_PATHS_USER_COUNT](state, { id, userCount }) {
    upateNodePathsUserCount(state.deptTree, { id, userCount });
  },
  [UPDATE_SUB_DEPT_ORDER](state, { parentId, orderIds }) {
    updateSubNodeOrder(state.deptTree, { parentId, orderIds });
  },
  [INIT_ROLES](state, roleList) {
    state.roleList = roleList;
  },
  [SET_CURRENT_ROLE_ID](state, roleId) {
    state.currentRoleId = roleId;
  },
  [UPDATE_ROLE](state, role) {
    state.roleList = state.roleList.map(item => {
      if (item.id !== role.id) {
        return item;
      }
      return {
        ...item,
        ...role,
      };
    });
  },
  [DELETE_ROLE](state, roleId) {
    state.roleList = state.roleList.filter(item => item.id !== roleId);
  },
  [RESET](state) {
    Object.assign(state, cloneDeep(initState));
  },
};

const actions = {
  async fetchDeptTree({ commit }) {
    commit(UPDATE_DEPT_LOADING, true);
    try {
      const depts = await getDeptList({
        showUserCount: true,
        showUser: false,
      });
      // 添加树层级字段level
      formatTreeLevel(depts);
      // 添加节点展开、收起和选中状态
      formatTree(depts);
      commit(UPDATE_DEPT_LOADING, false);
      commit(INIT_DEPTS, depts);
      commit(SET_CURRENT_DEPT_ID, depts.id);
    } catch (err) {
      commit(UPDATE_DEPT_LOADING, false);
      ZP.messageError(err.message);
    }
  },
  async fetchRoleList({ commit }) {
    commit(UPDATE_ROLE_LOADING, true);
    try {
      const { list } = await getRoleList();
      commit(UPDATE_ROLE_LOADING, false);
      commit(INIT_ROLES, list);
    } catch (err) {
      commit(UPDATE_ROLE_LOADING, false);
      ZP.messageError(err.message);
    }
  },
};

const getters = {
  deptLoading(state) {
    return state.deptLoading;
  },
  roleLoading(state) {
    return state.roleLoading;
  },
  deptTree(state) {
    return state.deptTree;
  },
  currentDeptId(state) {
    return state.currentDeptId;
  },
  currentRoleId(state) {
    return state.currentRoleId;
  },
  deptPaths(state) {
    return findNodeFullPath(state.deptTree, state.currentDeptId);
  },
  subDepts(state) {
    return findSubNodes(state.deptTree, state.currentDeptId);
  },
  roleList(state) {
    return state.roleList;
  },
  getRole: state => roleId => state.roleList.find(item => item.id === roleId) || {},
};

export default {
  name: 'crm',
  namespaced: true,
  state: cloneDeep(initState),
  mutations,
  actions,
  getters,
};
